import { isAndroid, osVersion, mobileModel } from "react-device-detect";

// Chrome 110+ (released Feb 2023) applies User-Agent Reduction on Android:
// the legacy UA string is frozen to always say "Android 10" with device
// model "K", regardless of the actual OS version or device. Example:
//
//   Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 ...
//
// If we see this exact combination, we know:
//   1. The browser is Chrome 110+
//   2. The real Android version is hidden
//   3. Statistically this is almost always Android 11+ — real Android 10
//      is 7 years old and the intersection of "still on Android 10" and
//      "has Chrome 110+" is vanishingly small
//
// So we treat the frozen UA as "modern Android, show the Quick Settings
// message." Real Android 10/11 devices with a genuine (non-frozen) UA
// will have a real mobileModel like "Pixel 4" or "SM-G970F" — NOT "K" —
// and get routed to the chrome_permission_legacy variant instead.
//
// This detection is fully synchronous, works in every browser and
// WebView/iframe context, and doesn't depend on User-Agent Client Hints.

const _isChromeFrozenUA =
  isAndroid &&
  String(osVersion) === "10" &&
  String(mobileModel).toUpperCase() === "K";

const _legacyMajor = parseInt(String(osVersion).split(".")[0], 10);

/**
 * Returns the camera-fallback portal variant to show.
 *
 *   "os_toggle_12plus"         — Android 12+ (or frozen UA assumed modern):
 *                                 show Quick Settings toggle steps.
 *   "chrome_permission_legacy" — Real Android 10/11: show Chrome address-bar
 *                                 site permission steps.
 *   "generic"                  — Not Android (iOS, desktop, anything else):
 *                                 show only the camera-app fallback button
 *                                 and refresh — no device-specific steps.
 *
 * Previously returned null for non-Android, but we now offer the camera-app
 * fallback to all users — the generic variant just hides the
 * Android-specific Quick Settings / Chrome instructions.
 */
export function getAndroidBlockVariant() {
  if (!isAndroid) return "generic";

  // Chrome 110+ frozen UA — assume modern Android with the OS toggle.
  if (_isChromeFrozenUA) return "os_toggle_12plus";

  // Genuine UA reports Android 12 or later — device has the OS toggle.
  if (!Number.isNaN(_legacyMajor) && _legacyMajor >= 12) {
    return "os_toggle_12plus";
  }

  // Genuine Android 10/11 — no OS toggle exists on these versions.
  return "chrome_permission_legacy";
}

/**
 * True if the user should see the camera-fallback portal on a camera
 * error. Now returns true for every device — the portal's generic
 * variant is a universally-useful fallback that lets the user take a
 * photo via the OS camera app / file picker when getUserMedia fails.
 *
 * Kept exported for backward compat with the call sites in capture.js
 * files that previously gated Android-specific behavior on it.
 */
export function shouldShowAndroidCameraBlock() {
  return true;
}

// Exported for telemetry/debugging — consumers can include this in an
// event property to see how often the frozen-UA path is hit.
export const isChromeFrozenUA = _isChromeFrozenUA;
